import { qs, cssMs, prefersReducedMotion } from '../core/dom.js';

// Homepage hero intro — spinner cover + staggered text (HoD port).
// Binds [data-hero="home"]; no-ops when absent.

const MAX_STEP = 5;

export function initLprHero() {
	const root = qs('[data-hero="home"]');
	if (!root) return;

	document.documentElement.classList.add('anim');

	const visual = qs('.hero-visual', root);
	const content = qs('.hero-text', root);
	const firstImg = qs('.hero-visual img', root);
	const rule = qs('[data-hero-rule]');
	if (!visual) return;

	let revealed = false;
	let revealStarted = false;
	let coverDelayTimeout = null;
	let coverGrayTimeout = null;
	let coverFadeTimeout = null;
	let spinnerFadeTimeout = null;
	let coverFinishTimeout = null;
	let ruleDrawTimeout = null;
	let coverFadeEndHandler = null;
	let awaitingReplay = false;
	let heroHasBeenVisible = false;
	let statsHasBeenVisible = false;
	let contentReleaseTimeout = null;

	const visualMs = (prop, fallback) => cssMs(visual, prop, fallback);
	const contentMs = (prop, fallback) => (content ? cssMs(content, prop, fallback) : fallback);

	const introTotalMs = () => {
		const delayMs = contentMs('--hero-text-delay', 0);
		const stepMs = contentMs('--hero-text-step', 1500);
		const gapMs = contentMs('--hero-text-gap', 150);
		return delayMs + MAX_STEP * gapMs + stepMs + 40;
	};

	const clearContentTimeouts = () => {
		if (contentReleaseTimeout) {
			clearTimeout(contentReleaseTimeout);
			contentReleaseTimeout = null;
		}
	};

	const releaseContentAfter = (ms) => {
		contentReleaseTimeout = setTimeout(() => {
			contentReleaseTimeout = null;
			content?.classList.remove('is-animating');
		}, ms);
	};

	const snapContentToHidden = () => {
		if (!content) return;
		content.classList.remove('is-revealed', 'is-replaying-out');
		content.classList.add('is-resetting');
		void content.offsetWidth;
		content.classList.remove('is-resetting');
	};

	const startContentIntro = () => {
		if (!content) return;
		snapContentToHidden();
		void content.offsetWidth;
		content.classList.add('is-revealed');
		releaseContentAfter(introTotalMs());
	};

	// --- Cover sequence -------------------------------------------------------

	const beginSpinnerFade = () => visual.classList.add('is-spinner-fading');
	const beginCoverGray = () => visual.classList.add('is-cover-graying');

	const snapRuleToHidden = () => {
		if (!rule) return;
		rule.classList.remove('is-drawn', 'is-stats-in');
		rule.classList.add('is-resetting');
		void rule.offsetWidth;
		rule.classList.remove('is-resetting');
	};

	const startStatsIntro = () => {
		if (!rule) return;
		rule.classList.remove('is-stats-in');
		rule.classList.add('is-resetting');
		void rule.offsetWidth;
		rule.classList.remove('is-resetting');
		void rule.offsetWidth;
		rule.classList.add('is-stats-in');
	};

	const beginRuleDraw = () => rule?.classList.add('is-drawn');

	const scheduleRuleDraw = () => {
		if (ruleDrawTimeout) {
			clearTimeout(ruleDrawTimeout);
			ruleDrawTimeout = null;
		}

		const delayMs = rule ? cssMs(rule, '--hero-rule-delay', 280) : 280;
		ruleDrawTimeout = setTimeout(() => {
			ruleDrawTimeout = null;
			beginRuleDraw();
		}, delayMs);
	};

	const beginCoverFade = () => {
		const backdrop = qs('.hero-cover__backdrop', visual);
		const fadeMs = visualMs('--hero-cover-fade', 1500);

		visual.classList.add('is-revealed', 'is-cover-fading');

		const finish = () => {
			revealed = true;
			coverFinishTimeout = null;
		};

		if (coverFinishTimeout) {
			clearTimeout(coverFinishTimeout);
			coverFinishTimeout = null;
		}

		if (backdrop) {
			if (coverFadeEndHandler) {
				backdrop.removeEventListener('transitionend', coverFadeEndHandler);
			}
			coverFadeEndHandler = (e) => {
				if (e.target !== backdrop || e.propertyName !== 'opacity') return;
				backdrop.removeEventListener('transitionend', coverFadeEndHandler);
				coverFadeEndHandler = null;
				finish();
			};
			backdrop.addEventListener('transitionend', coverFadeEndHandler);
		}
		coverFinishTimeout = setTimeout(finish, fadeMs + 100);
	};

	const scheduleSequence = ({ force = false, replay = false } = {}) => {
		if (revealStarted && !force) return;
		revealStarted = true;

		[coverDelayTimeout, coverGrayTimeout, coverFadeTimeout, spinnerFadeTimeout].forEach((t) => {
			if (t) clearTimeout(t);
		});
		coverDelayTimeout = coverGrayTimeout = coverFadeTimeout = spinnerFadeTimeout = null;

		const panelHoldMs = visualMs('--hero-cover-panel-hold', 300);
		spinnerFadeTimeout = setTimeout(() => {
			spinnerFadeTimeout = null;
			beginSpinnerFade();
		}, panelHoldMs);

		if (replay) {
			const delayMs = visualMs('--hero-cover-delay-replay', 100);
			coverDelayTimeout = setTimeout(() => {
				coverDelayTimeout = null;
				beginCoverFade();
			}, delayMs);
			return;
		}

		const grayHoldMs = visualMs('--hero-cover-gray-duration', 400);
		coverGrayTimeout = setTimeout(() => {
			coverGrayTimeout = null;
			beginCoverGray();
			coverFadeTimeout = setTimeout(() => {
				coverFadeTimeout = null;
				beginCoverFade();
			}, grayHoldMs);
		}, panelHoldMs);
	};

	const clearAllTimers = () => {
		clearContentTimeouts();
		[coverDelayTimeout, coverGrayTimeout, coverFadeTimeout, spinnerFadeTimeout, coverFinishTimeout, ruleDrawTimeout].forEach((t) => {
			if (t) clearTimeout(t);
		});
		coverDelayTimeout = coverGrayTimeout = coverFadeTimeout = spinnerFadeTimeout = coverFinishTimeout = ruleDrawTimeout = null;

		const backdrop = qs('.hero-cover__backdrop', visual);
		if (backdrop && coverFadeEndHandler) {
			backdrop.removeEventListener('transitionend', coverFadeEndHandler);
			coverFadeEndHandler = null;
		}
	};

	const snapToRevealed = () => {
		visual.classList.remove('is-resetting');
		visual.classList.add('is-revealed', 'is-cover-fading');
		rule?.classList.add('is-drawn', 'is-stats-in');
		revealed = true;
		revealStarted = true;
		content?.classList.remove('is-animating', 'is-replaying-out', 'is-resetting');
		content?.classList.add('is-revealed');
	};

	const resetHero = () => {
		clearAllTimers();

		visual.classList.remove('is-revealed', 'is-cover-fading', 'is-cover-graying', 'is-spinner-fading');
		visual.classList.add('is-resetting');
		void visual.offsetWidth;
		visual.classList.remove('is-resetting');

		revealed = false;
		revealStarted = false;

		snapContentToHidden();
		content?.classList.remove('is-animating');

		awaitingReplay = true;
	};

	const reset = () => {
		resetHero();
		snapRuleToHidden();
	};

	const playIntro = () => {
		if (!awaitingReplay) return;

		if (prefersReducedMotion()) {
			snapToRevealed();
			awaitingReplay = false;
			return;
		}

		content?.classList.add('is-animating');
		startContentIntro();
		startStatsIntro();
		scheduleRuleDraw();
		scheduleSequence({ force: true, replay: true });
		awaitingReplay = false;
	};

	const replayIntro = () => {
		if (prefersReducedMotion()) return;

		reset();
		content?.classList.add('is-animating');
		startContentIntro();
		startStatsIntro();
		scheduleRuleDraw();
		scheduleSequence({ force: true, replay: false });
		awaitingReplay = false;
	};

	const onReplayClick = (e) => {
		if (e.target.closest('a, button, .hero-ctas')) return;
		replayIntro();
	};

	// --- Wire up --------------------------------------------------------------

	if (prefersReducedMotion()) {
		snapToRevealed();
		return;
	}

	content?.addEventListener('click', onReplayClick);
	visual.addEventListener('click', onReplayClick);
	rule?.addEventListener('click', onReplayClick);

	content?.classList.add('is-animating');
	startContentIntro();
	startStatsIntro();
	scheduleRuleDraw();

	if (firstImg?.complete && firstImg.naturalWidth > 0) {
		scheduleSequence();
	} else if (firstImg) {
		firstImg.addEventListener('load', () => scheduleSequence(), { once: true });
		firstImg.addEventListener('error', () => scheduleSequence(), { once: true });
		setTimeout(() => scheduleSequence(), visualMs('--hero-image-load-timeout', 5000));
	} else {
		scheduleSequence();
	}

	const offscreenObserver = new IntersectionObserver(
		(entries) => {
			entries.forEach((e) => {
				if (e.isIntersecting) {
					heroHasBeenVisible = true;
					return;
				}
				if (heroHasBeenVisible) resetHero();
			});
		},
		{ threshold: 0, rootMargin: '0px' }
	);

	const statsOffscreenObserver = rule
		? new IntersectionObserver(
			(entries) => {
				entries.forEach((e) => {
					if (e.isIntersecting) {
						statsHasBeenVisible = true;
						return;
					}
					if (statsHasBeenVisible) snapRuleToHidden();
				});
			},
			{ threshold: 0, rootMargin: '0px' }
		)
		: null;

	const enterObserver = new IntersectionObserver(
		(entries) => {
			entries.forEach((e) => {
				if (e.isIntersecting && awaitingReplay) playIntro();
			});
		},
		{ threshold: 0, rootMargin: '0px' }
	);

	offscreenObserver.observe(root);
	statsOffscreenObserver?.observe(rule);
	enterObserver.observe(root);
	if (rule) enterObserver.observe(rule);
}
